import React, { useEffect, useMemo, useRef, useState } from 'react';
import Icon from './Icon';
import { filtrarPorBusca, itemExato } from '../services/comboRanking';

// ─────────────────────────────────────────────────────────────────────────────
// Combo com busca — o "NT combo" do 1.4.1.
//
// Um `<select>` com 400 classificações é uma lista para rolar. Aqui se digita e
// a lista filtra, com o mais provável no topo (ver services/comboRanking.js).
//
// Duas coisas que parecem detalhe e não são:
//
//   · o CÓDIGO é o que vai para o MySuite, não o texto. Enquanto o que está
//     escrito não casar exatamente com um item, o código fica inválido — senão
//     dava para editar o texto e mandar o código do item anterior;
//   · a seleção acontece no `mousedown`, não no `click`. O `blur` do input
//     dispara antes do `click` e fecharia a lista, engolindo a escolha.
// ─────────────────────────────────────────────────────────────────────────────

export default function ComboBusca({
  itens = [],           // [{ codigo, nome }]
  valor = '',           // o texto visível
  codigo = '',          // o código escolhido
  aoEscolher,           // (codigo, nome) => void
  placeholder = 'Digite para buscar…',
  desabilitado = false,
  id,
}) {
  const [aberto, setAberto] = useState(false);
  const [ativo, setAtivo] = useState(0);
  const listaRef = useRef(null);
  const fechando = useRef(null);

  const filtrados = useMemo(() => filtrarPorBusca(itens, valor), [itens, valor]);

  useEffect(() => () => clearTimeout(fechando.current), []);

  // Mantém o item ativo à vista ao navegar pelo teclado.
  useEffect(() => {
    if (!aberto || !listaRef.current) return;
    const el = listaRef.current.children[ativo];
    if (el) el.scrollIntoView({ block: 'nearest' });
  }, [ativo, aberto]);

  const escolher = (item) => {
    aoEscolher(String(item.codigo), item.nome);
    setAberto(false);
  };

  const aoDigitar = (texto) => {
    // O código só sobrevive se o texto continuar batendo com um item real.
    const exato = itemExato(itens, texto);
    aoEscolher(exato ? String(exato.codigo) : '', texto);
    setAberto(true);
    setAtivo(0);
  };

  const aoTeclar = (e) => {
    if (!aberto && (e.key === 'ArrowDown' || e.key === 'Enter')) { setAberto(true); return; }
    if (!aberto) return;

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setAtivo(i => Math.min(filtrados.length - 1, i + 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setAtivo(i => Math.max(0, i - 1));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (filtrados[ativo]) escolher(filtrados[ativo]);
    } else if (e.key === 'Escape') {
      setAberto(false);
    }
  };

  return (
    <div className="nt-combo">
      <input
        id={id}
        type="text"
        value={valor}
        onChange={e => aoDigitar(e.target.value)}
        onFocus={() => setAberto(true)}
        // O blur precisa esperar o mousedown do item, senão a lista fecha antes
        // de a escolha ser registrada.
        onBlur={() => { fechando.current = setTimeout(() => setAberto(false), 150); }}
        onKeyDown={aoTeclar}
        placeholder={placeholder}
        disabled={desabilitado}
        autoComplete="off"
      />
      {/* Sinal de que o que está escrito corresponde a um item de verdade. */}
      {codigo
        ? <span className="nt-combo-ok" title={`Código ${codigo}`}><Icon name="check" size={12} /></span>
        : valor ? <span className="nt-combo-alerta" title="Escolha um item da lista"><Icon name="alert-circle" size={12} /></span> : null}

      {aberto && (
        <div className="nt-combo-lista" ref={listaRef}>
          {filtrados.length === 0 && <div className="nt-combo-vazio">Nada encontrado.</div>}
          {filtrados.map((item, i) => (
            <button
              type="button"
              key={item.codigo}
              className={`nt-combo-item ${i === ativo ? 'ativo' : ''}`}
              onMouseDown={(e) => { e.preventDefault(); escolher(item); }}
              onMouseEnter={() => setAtivo(i)}
            >
              <span className="nt-combo-nome">{item.nome}</span>
              <span className="nt-combo-cod">{item.codigo}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
